import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import api from '../services/api';
import { useAuth } from './AuthContext';

const CompanyContext = createContext(null);

export function CompanyProvider({ children }) {
  const { user, tenant } = useAuth();
  const [companies, setCompanies] = useState([]);
  const [activeCompany, setActiveCompany] = useState(null);
  const [scanStatus, setScanStatus] = useState(null);
  const [scanResults, setScanResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const pollCountRef = useRef(0);

  // Load companies once user is authenticated
  useEffect(() => {
    if (user) loadCompanies();
    else if (user === null) setLoading(false); // not logged in — stop spinner
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  // F4: Poll with exponential backoff (3s → 5s → 8s → 10s max)
  useEffect(() => {
    if (!scanning || !activeCompany) { pollCountRef.current = 0; return; }
    let cancelled = false;

    const poll = async () => {
      if (cancelled) return;
      try {
        const status = await api.getScanStatus(activeCompany.company_id);
        if (cancelled) return;
        setScanStatus(status);
        if (status && (status.status === 'completed' || status.status === 'failed')) {
          setScanning(false);
          if (status.status === 'completed') {
            const results = await api.getScanResults(activeCompany.company_id);
            if (!cancelled) setScanResults(results);
          }
          return;
        }
      } catch {
        // ignore polling errors
      }
      pollCountRef.current += 1;
      const delay = Math.min(3000 + pollCountRef.current * 1000, 10000);
      if (!cancelled) setTimeout(poll, delay);
    };

    const timer = setTimeout(poll, 3000);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [scanning, activeCompany]);

  const loadCompanies = async () => {
    try {
      const list = await api.listCompanies();
      setCompanies(list);

      let comp = list[0] ?? null;

      // ── Auto-provision: no company yet → create one from tenant domain ──
      if (!comp && tenant?.domain) {
        try {
          const companyName = tenant.company_name || tenant.domain;
          console.log('[CompanyContext] No company found — auto-creating for', tenant.domain);
          comp = await api.createCompany(companyName, tenant.domain);
          setCompanies([comp]);
        } catch (e) {
          console.error('[CompanyContext] Auto-company creation failed:', e);
        }
      }

      if (comp && !activeCompany) {
        setActiveCompany(comp);
        // Load existing results
        let hasRealResults = false;
        try {
          const results = await api.getScanResults(comp.company_id);
          if (results && results.scan && results.assets && results.assets.length > 0) {
            setScanResults(results);
            hasRealResults = true;
          }
        } catch {
          // no results yet
        }
        let scanRunning = false;
        let scanEverRan = false;
        try {
          const status = await api.getScanStatus(comp.company_id);
          setScanStatus(status);
          if (status && status.scan_id) scanEverRan = true;
          if (status && (status.status === 'running' || status.status === 'pending')) {
            setScanning(true);
            scanRunning = true;
          }
        } catch {
          // no scan yet
        }
        // Only auto-trigger if NO scan has ever run for this company
        if (!hasRealResults && !scanRunning && !scanEverRan) {
          try {
            console.log('[CompanyContext] Auto-triggering first scan for', comp.domain);
            setScanning(true);
            const status = await api.startScan(comp.company_id);
            setScanStatus(status);
          } catch (e) {
            console.error('[CompanyContext] Auto-scan trigger failed:', e);
            setScanning(false);
          }
        }
      }
    } catch (e) {
      console.error('Failed to load companies:', e);
    } finally {
      setLoading(false);
    }
  };

  const addCompany = async (name, domain) => {
    const company = await api.createCompany(name, domain);
    setCompanies(prev => [...prev.filter(c => c.company_id !== company.company_id), company]);
    setActiveCompany(company);
    setScanResults(null);
    setScanStatus(null);
    return company;
  };

  const startScan = useCallback(async () => {
    if (!activeCompany) return;
    setScanning(true);
    setScanResults(null);
    const status = await api.startScan(activeCompany.company_id);
    setScanStatus(status);
    return status;
  }, [activeCompany]);

  const refreshResults = useCallback(async () => {
    if (!activeCompany) return;
    try {
      const results = await api.getScanResults(activeCompany.company_id);
      setScanResults(results);
    } catch {
      // ignore
    }
  }, [activeCompany]);

  return (
    <CompanyContext.Provider value={{
      companies,
      activeCompany,
      setActiveCompany,
      scanStatus,
      scanResults,
      loading,
      scanning,
      addCompany,
      startScan,
      refreshResults,
      loadCompanies,
    }}>
      {children}
    </CompanyContext.Provider>
  );
}

export function useCompany() {
  const ctx = useContext(CompanyContext);
  if (!ctx) throw new Error('useCompany must be used within CompanyProvider');
  return ctx;
}
